// src/pages/WagesPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useData } from "../DataContext";
import { generateId } from "../utils/id";
import { WageEntry } from "../data/mockData";
import { projectDisplayLabelPretty } from "../utils/format";

import { Card } from "../components/ui/Card";
import { useTranslation } from "react-i18next";
import { TableShell, CellClamp } from "../components/ui/TableShell";

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

/**
 * ISO week date range (Mon → Sun).
 * Week 1 is the week with Jan 4th in it.
 */
function getISOWeekRange(year: number, week: number) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7; // Sun=7
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));

  const start = new Date(week1Mon);
  start.setUTCDate(week1Mon.getUTCDate() + (week - 1) * 7);

  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);

  return { start, end };
}

function fmtShort(d: Date, locale?: string) {
  const yyyy = d.getUTCFullYear();
  const mm = d.getUTCMonth();
  const dd = d.getUTCDate();
  const local = new Date(yyyy, mm, dd);
  return local.toLocaleDateString(locale || undefined, { month: "short", day: "2-digit" });
}

function getISOWeekNumber(date: Date) {
  // Uses UTC so it matches getISOWeekRange()
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // Mon=1..Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // nearest Thursday

  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((+d - +yearStart) / 86400000 + 1) / 7);
  return weekNo;
}

function weekLabelWithDates(year: number, weekNumber: number, weekWord = "Week", locale?: string) {
  const { start, end } = getISOWeekRange(year, weekNumber);
  return `${year} — ${weekWord} ${pad2(weekNumber)} (${fmtShort(start, locale)} — ${fmtShort(end, locale)})`;
}

function buildSelectableWeeks(wages: WageEntry[], currentYear: number) {
  const weeks = new Map<string, { year: number; weekNumber: number }>();

  wages.forEach((w) => {
    const year = Number(w.year) || 0;
    const weekNumber = Number(w.weekNumber) || 0;
    if (year > 0 && weekNumber >= 1 && weekNumber <= 53) {
      weeks.set(`${year}-${weekNumber}`, { year, weekNumber });
    }
  });

  // Empty weeks must remain selectable so gaps in the ledger are visible.
  for (let weekNumber = 1; weekNumber <= 53; weekNumber += 1) {
    weeks.set(`${currentYear}-${weekNumber}`, { year: currentYear, weekNumber });
  }

  return Array.from(weeks.values()).sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.weekNumber - a.weekNumber;
  });
}

function parseOptionalNumber(input: string): number | null {
  const t = (input ?? "").trim();
  if (t === "") return null;
  const n = Number(t);
  if (Number.isNaN(n)) return null;
  return n;
}

function IconPencil({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconTrash({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 6l1 16h8l1-16" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
const DAY_ORDER: DayKey[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABEL_KEY: Record<DayKey, string> = {
  mon: "wages.dayMon",
  tue: "wages.dayTue",
  wed: "wages.dayWed",
  thu: "wages.dayThu",
  fri: "wages.dayFri",
  sat: "wages.daySat",
  sun: "wages.daySun",
};

function flagsFromEntry(w: WageEntry) {
  return {
    mon: Number(w.mon) ? 1 : 0,
    tue: Number(w.tue) ? 1 : 0,
    wed: Number(w.wed) ? 1 : 0,
    thu: Number(w.thu) ? 1 : 0,
    fri: Number(w.fri) ? 1 : 0,
    sat: Number(w.sat) ? 1 : 0,
    sun: Number(w.sun) ? 1 : 0,
  };
}

function flagsFromForm(mon: boolean, tue: boolean, wed: boolean, thu: boolean, fri: boolean, sat: boolean, sun: boolean) {
  return {
    mon: mon ? 1 : 0,
    tue: tue ? 1 : 0,
    wed: wed ? 1 : 0,
    thu: thu ? 1 : 0,
    fri: fri ? 1 : 0,
    sat: sat ? 1 : 0,
    sun: sun ? 1 : 0,
  };
}

function hasAnyDayFlag(flags: Record<DayKey, number>) {
  return DAY_ORDER.some((key) => Number(flags[key]) === 1);
}

export default function WagesPage() {
  const { t, i18n } = useTranslation();
  const { resources, projects, clients, wageEntries, addWageEntry, updateWageEntry, deleteWageEntry } = useData();
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentWeek = getISOWeekNumber(now);

  // ✅ only show true “resources” (exclude subcontractors)
  const workers = useMemo(() => {
    return resources.filter((r) => Number((r as any).isSubcontractor || 0) !== 1);
  }, [resources]);

  /* ======================
     Add Wage Entry form state
  ====================== */
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [weekNumber, setWeekNumber] = useState<number>(() => getISOWeekNumber(new Date()));
  const [resourceId, setResourceId] = useState<string>("");
  const [projectId, setProjectId] = useState<string>("");

  // Day checkboxes
  const [mon, setMon] = useState<boolean>(false);
  const [tue, setTue] = useState<boolean>(false);
  const [wed, setWed] = useState<boolean>(false);
  const [thu, setThu] = useState<boolean>(false);
  const [fri, setFri] = useState<boolean>(false);
  const [sat, setSat] = useState<boolean>(false);
  const [sun, setSun] = useState<boolean>(false);

  // ✅ blankable optional adjustments
  const [adjustments, setAdjustments] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  // Manual override
  const [manualDaysWorked, setManualDaysWorked] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);

  const selectedWorker = workers.find((r) => r.id === resourceId);
  const dayRate = Number((selectedWorker as any)?.dayRate || 0);

  const checkboxDaysWorked = useMemo(() => {
    const checks = [mon, tue, wed, thu, fri, sat, sun];
    return checks.reduce((s, x) => s + (x ? 1 : 0), 0);
  }, [mon, tue, wed, thu, fri, sat, sun]);

  const effectiveDaysWorked = useMemo(() => {
    const md = parseOptionalNumber(manualDaysWorked);
    if (md === null) return checkboxDaysWorked;
    return Math.max(0, md);
  }, [manualDaysWorked, checkboxDaysWorked]);

  const adjustmentsNum = useMemo(() => parseOptionalNumber(adjustments) ?? 0, [adjustments]);

  const computedTotal = useMemo(() => {
    const base = effectiveDaysWorked * dayRate;
    return Math.max(base + adjustmentsNum, 0);
  }, [effectiveDaysWorked, dayRate, adjustmentsNum]);

  const handleAdd = () => {
    if (!resourceId || !projectId || !year || !weekNumber) {
      setFormError(t("wages.formErrorRequired"));
      return;
    }

    const md = parseOptionalNumber(manualDaysWorked);
    const hasWork = checkboxDaysWorked > 0 || (md !== null && md > 0) || adjustmentsNum !== 0;

    if (!hasWork) {
      setFormError(t("wages.formErrorNoDays"));
      return;
    }

    // ✅ enforce day flags so we can prevent duplicates
    const newFlags = flagsFromForm(mon, tue, wed, thu, fri, sat, sun);
    if (!hasAnyDayFlag(newFlags)) {
      setFormError(t("wages.formErrorNoDayFlags"));
      return;
    }

    // ✅ duplicate prevention (same worker + same ISO week + overlapping day)
    // ✅ Consolidation rule (SAFE):
    // One entry per (worker + year + week + project).
    // If they need to add more days, they should edit the existing entry later (Phase 2).
    const existingSameKey = wageEntries.find(
      (w) =>
        w.resourceId === resourceId &&
        w.projectId === projectId &&
        Number(w.year) === Number(year) &&
        Number(w.weekNumber) === Number(weekNumber)
    );

    if (existingSameKey) {
      const workerName = workers.find((r) => r.id === resourceId)?.name || "Worker";
      const projectName = getProjectPretty(projectId);
      setFormError(
        t("wages.duplicateBlocked", { worker: workerName, project: projectName, week: weekLabelWithDates(Number(year), Number(weekNumber), t("wages.weekLabel"), i18n.language) })
      );
      return;
    }

    const entry: WageEntry = {
      id: generateId("WAGE"),
      year: Number(year),
      weekNumber: Number(weekNumber),
      resourceId,
      projectId,

      mon: newFlags.mon,
      tue: newFlags.tue,
      wed: newFlags.wed,
      thu: newFlags.thu,
      fri: newFlags.fri,
      sat: newFlags.sat,
      sun: newFlags.sun,

      daysWorked: Number(effectiveDaysWorked) || 0,
      totalWages: Number(computedTotal.toFixed(2)),
      adjustments: adjustmentsNum,
      description: description.trim() ? description.trim() : undefined,
      paid: false,
    };

    addWageEntry(entry);

    // reset day inputs + optional fields (keep worker/project/week for speed)
    setMon(false);
    setTue(false);
    setWed(false);
    setThu(false);
    setFri(false);
    setSat(false);
    setSun(false);
    setAdjustments("");
    setDescription("");
    setManualDaysWorked("");
    setFormError(null);
  };

  const resetAddForm = () => {
    setResourceId("");
    setProjectId("");
    setYear(new Date().getFullYear());
    setWeekNumber(1);

    setMon(false);
    setTue(false);
    setWed(false);
    setThu(false);
    setFri(false);
    setSat(false);
    setSun(false);

    setAdjustments("");
    setDescription("");
    setManualDaysWorked("");
    setFormError(null);
  };

  /* ======================
     Week options (1—53)
  ====================== */
  const weekOptions = useMemo(() => {
    const y = Number(year) || new Date().getFullYear();
    return Array.from({ length: 53 }, (_, i) => i + 1).map((w) => ({
      value: w,
      label: weekLabelWithDates(y, w, t("wages.weekLabel"), i18n.language),
    }));
  }, [year, t, i18n.language]);

  /* ======================
     Pay This Week card
  ====================== */
  const availableWeeks = useMemo(() => {
    return buildSelectableWeeks(wageEntries, currentYear);
  }, [wageEntries, currentYear]);

   const [payWeekKey, setPayWeekKey] = useState<string>(() => {
    const now = new Date();
    const y = now.getFullYear();
    const w = getISOWeekNumber(now);
    return `${y}-${w}`;
   });


    useEffect(() => {
      if (!availableWeeks.length) return;

      const hasCurrent = availableWeeks.some((w) => `${w.year}-${w.weekNumber}` === payWeekKey);
      if (!hasCurrent) setPayWeekKey(`${availableWeeks[0].year}-${availableWeeks[0].weekNumber}`);
    }, [availableWeeks, payWeekKey]);


  const payWeek = useMemo(() => {
    const [y, w] = payWeekKey.split("-").map((x) => Number(x));
    return { year: y || new Date().getFullYear(), weekNumber: w || 1 };
  }, [payWeekKey]);

  const unpaidForPayWeek = useMemo(() => {
    return wageEntries.filter((w) => w.year === payWeek.year && w.weekNumber === payWeek.weekNumber && !w.paid);
  }, [wageEntries, payWeek.year, payWeek.weekNumber]);

  const unpaidForPayWeekIds = useMemo<string[]>(
    () => unpaidForPayWeek.map((w: WageEntry) => w.id),
    [unpaidForPayWeek]
  );

  const payCardRows = useMemo(() => {
    const byRes: Record<
      string,
      {
        resourceId: string;
        workerName: string;
        total: number;
        entryIds: string[];
        projects: Set<string>;
      }
    > = {};

    unpaidForPayWeek.forEach((w) => {
      if (!byRes[w.resourceId]) {
        const workerName = resources.find((r) => r.id === w.resourceId)?.name || w.resourceId;
        byRes[w.resourceId] = {
          resourceId: w.resourceId,
          workerName,
          total: 0,
          entryIds: [],
          projects: new Set(),
        };
      }
      byRes[w.resourceId].total += Number(w.totalWages) || 0;
      byRes[w.resourceId].entryIds.push(w.id);
      byRes[w.resourceId].projects.add(w.projectId);
    });

    return Object.values(byRes).sort((a, b) => b.total - a.total);
  }, [unpaidForPayWeek, resources]);

    const payWeekOverlapWarnings = useMemo(() => {
    // Detect risky pattern: same worker has overlapping day flags across multiple unpaid entries in this payWeek.
    const warnings: { workerName: string; days: string[]; entryCount: number; projectCount: number }[] = [];

    for (const row of payCardRows) {
      const entries = unpaidForPayWeek.filter((w) => w.resourceId === row.resourceId);
      if (entries.length <= 1) continue;

      const counts: Record<DayKey, number> = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };

      entries.forEach((w) => {
        const f = flagsFromEntry(w);
        (Object.keys(counts) as DayKey[]).forEach((k) => {
          if (Number(f[k]) === 1) counts[k] += 1;
        });
      });

      const overlapping = DAY_ORDER.filter((key) => counts[key] >= 2).map((key) => t(DAY_LABEL_KEY[key]));
      if (!overlapping.length) continue;

      warnings.push({
        workerName: row.workerName,
        days: overlapping,
        entryCount: entries.length,
        projectCount: row.projects.size,
      });
    }

    return warnings;
  }, [payCardRows, unpaidForPayWeek, t]);

  const markEntriesPaid = (ids: string[], paidMethod?: "Cash" | "Bank") => {
    const today = new Date().toISOString().slice(0, 10);
    const paidNotes = `Marked paid via Pay This Week (${paidMethod || "Company"})`;

    const toUpdate = wageEntries.filter((w) => ids.includes(w.id));
    if (!toUpdate.length) return;

    const ok = window.confirm(t("wages.markPaidConfirm", { count: toUpdate.length }));
    if (!ok) return;

    toUpdate.forEach((w) => {
      updateWageEntry({
        ...w,
        paid: true,
        paidDate: today,
        paidMethod,
        paidNotes,
      } as any);
    });
  };

  /* ======================
     List view (ledger)
  ====================== */

  const workerNameById = useMemo(() => {
    const m = new Map<string, string>();
    resources.forEach((r) => m.set(r.id, r.name));
    return m;
  }, [resources]);

  const projectPrettyById = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach((p) => {
      m.set(p.id, projectDisplayLabelPretty(p as any, clients as any));
    });
    return m;
  }, [projects, clients]);

  const getWorkerName = useCallback((rid: string) => workerNameById.get(rid) || rid, [workerNameById]);
  const getProjectPretty = useCallback((pid: string) => projectPrettyById.get(pid) || pid, [projectPrettyById]);

  const renderDaysOfWeek = (w: WageEntry) => {
    const f = flagsFromEntry(w);
    const onDays = DAY_ORDER.filter((key) => Number(f[key]) === 1);

    if (!onDays.length) return <span style={{ color: "var(--muted)" }}>—</span>;

    return (
      <div className="dowPills" title={onDays.map((key) => t(DAY_LABEL_KEY[key])).join(", ")}>
        {onDays.map((key) => (
          <span key={key} className="dowPill">
            {t(DAY_LABEL_KEY[key])}
          </span>
        ))}
      </div>
    );
  };


  // -------------------------
  // Ledger controls
  // -------------------------
  const [search, setSearch] = useState("");
  const [paidFilter, setPaidFilter] = useState<"All" | "Paid" | "Unpaid">("All");
  const [workerFilter, setWorkerFilter] = useState<string>("All");
  const [projectFilter, setProjectFilter] = useState<string>("All");

  
  // ✅ Weekly view filter (single dropdown)
  // Default to CURRENT ISO year/week (even if there are no rows)
  const [weekKeyFilter, setWeekKeyFilter] = useState<string>(() => `${currentYear}-${currentWeek}`);

  const visibleEntries = useMemo(() => {
    const q = search.trim().toLowerCase();

    return wageEntries
      .slice()
      .filter((w) => {
        if (paidFilter !== "All") {
          if (paidFilter === "Paid" && !w.paid) return false;
          if (paidFilter === "Unpaid" && w.paid) return false;
        }
        if (workerFilter !== "All" && w.resourceId !== workerFilter) return false;
        if (projectFilter !== "All" && w.projectId !== projectFilter) return false;
        if (weekKeyFilter !== "All") {
          const wk = `${w.year}-${w.weekNumber}`;
          if (wk !== weekKeyFilter) return false;
        }

        if (!q) return true;

        const hay = [
          w.id,
          getWorkerName(w.resourceId),
          getProjectPretty(w.projectId),
          weekLabelWithDates(w.year, w.weekNumber, t("wages.weekLabel"), i18n.language),
          w.daysWorked,
          w.totalWages,
          w.adjustments,
          w.description || "",
          w.paid ? "paid" : "unpaid",
          (w as any).paidMethod || "",
          (w as any).paidNotes || "",
          (w as any).paidDate || "",
        ]
          .join(" ")
          .toLowerCase();

        return hay.includes(q);
      })
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        if (a.weekNumber !== b.weekNumber) return b.weekNumber - a.weekNumber;
        return (b.id || "").localeCompare(a.id || "");
      });
    }, [wageEntries, search, paidFilter, workerFilter, projectFilter, weekKeyFilter, getWorkerName, getProjectPretty, t, i18n.language]);

  const totalsFiltered = useMemo(() => {
    const sum = (arr: WageEntry[]) => arr.reduce((s, w) => s + (Number(w.totalWages) || 0), 0);
    const paid = visibleEntries.filter((w) => w.paid);
    const unpaid = visibleEntries.filter((w) => !w.paid);
    return {
      total: sum(visibleEntries),
      paid: sum(paid),
      unpaid: sum(unpaid),
      rows: visibleEntries.length,
      days: visibleEntries.reduce((s, w) => s + (Number(w.daysWorked) || 0), 0),
    };
  }, [visibleEntries]);

    const availableWeekKeys = useMemo(
      () => availableWeeks.map((w) => `${w.year}-${w.weekNumber}`),
      [availableWeeks]
    );

    // If current selection disappears (rare), reset to current week
    useEffect(() => {
      if (!availableWeekKeys.includes(weekKeyFilter)) {
        setWeekKeyFilter(`${currentYear}-${currentWeek}`);
      }
    }, [availableWeekKeys, weekKeyFilter, currentYear, currentWeek]);

  const ledgerHeaderRight = (
    <div className="wageFilters">

      <div className="wageFilterItem">
        <label className="label" style={{ marginBottom: 6 }}>
          {t("wages.filterWeekLabel")}
        </label>
        <select value={weekKeyFilter} onChange={(e) => setWeekKeyFilter(e.target.value)} style={{ width: "100%" }}>
          <option value="All">{t("wages.allWeeks")}</option>
          {availableWeekKeys.map((k) => {
            const [y, w] = k.split("-").map(Number);
            return (
              <option key={k} value={k}>
                {weekLabelWithDates(y, w, t("wages.weekLabel"), i18n.language)}
              </option>
            );
          })}
        </select>
      </div>

      <div className="wageFilterItem wageFilterWeek">
        <label className="label" style={{ marginBottom: 6 }}>
          {t("common.project")}
        </label>
        <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} style={{ width: "100%" }}>
          <option value="All">{t("common.all")}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {projectDisplayLabelPretty(p as any, clients as any)}
            </option>
          ))}
        </select>
      </div>    

      <div className="wageFilterItem">
        <label className="label" style={{ marginBottom: 6 }}>
          {t("wages.workerLabel")}
        </label>
        <select value={workerFilter} onChange={(e) => setWorkerFilter(e.target.value)} style={{ width: "100%" }}>
          <option value="All">{t("common.all")}</option>
          {workers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>    

      <div className="wageFilterItem">
        <label className="label" style={{ marginBottom: 6 }}>
          {t("wages.paidLabel")}
        </label>
        <select value={paidFilter} onChange={(e) => setPaidFilter(e.target.value as any)} style={{ width: "100%" }}>
          <option value="All">{t("common.all")}</option>
          <option value="Paid">{t("wages.paidBadge")}</option>
          <option value="Unpaid">{t("wages.unpaidBadge")}</option>
        </select>
      </div>

       {(paidFilter !== "All" ||
        workerFilter !== "All" ||
        projectFilter !== "All" ||
        weekKeyFilter !== `${currentYear}-${currentWeek}`) && (

        <button
          className="btn btnSecondary btnSm"
          type="button"
          onClick={() => {
            setPaidFilter("All");
            setWorkerFilter("All");
            setProjectFilter("All");
            setWeekKeyFilter(`${currentYear}-${currentWeek}`);


          }}
          style={{ alignSelf: "end" }}
          title={t("wages.clearFilters")}
        >
          {t("wages.clearFilters")}
        </button>
      )}
    </div>
  );

  // -------------------------
  // Editing in ledger
  // -------------------------
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPaid, setEditPaid] = useState<boolean>(false);
  const [editPaidDate, setEditPaidDate] = useState<string>("");
  const [editPaidMethod, setEditPaidMethod] = useState<"" | "Cash" | "Bank">("");
  const [editPaidNotes, setEditPaidNotes] = useState<string>("");

  // ✅ Edit Days (Mon—Sun) + Manual Override (supports split-day correction)
  const [editMon, setEditMon] = useState(false);
  const [editTue, setEditTue] = useState(false);
  const [editWed, setEditWed] = useState(false);
  const [editThu, setEditThu] = useState(false);
  const [editFri, setEditFri] = useState(false);
  const [editSat, setEditSat] = useState(false);
  const [editSun, setEditSun] = useState(false);

  const [editManualDaysWorked, setEditManualDaysWorked] = useState<string>("");

  // ✅ Edit-mode calcs (must be AFTER editMon/editTue/... and editManualDaysWorked exist)
  const editCheckboxDaysWorked = useMemo(() => {
    const checks = [editMon, editTue, editWed, editThu, editFri, editSat, editSun];
    return checks.reduce((s, x) => s + (x ? 1 : 0), 0);
  }, [editMon, editTue, editWed, editThu, editFri, editSat, editSun]);

  const editEffectiveDaysWorked = useMemo(() => {
    const md = parseOptionalNumber(editManualDaysWorked);
    if (md === null) return editCheckboxDaysWorked;
    return Math.max(0, md);
  }, [editManualDaysWorked, editCheckboxDaysWorked]);

  const editComputedTotal = useCallback(
    (w: WageEntry) => {
      const adj = Number((w as any).adjustments || 0) || 0;
      const rate =
        Number((workers.find((r) => r.id === w.resourceId) as any)?.dayRate || 0) || 0;

      const base = editEffectiveDaysWorked * rate;
      return Math.max(base + adj, 0);
    },
    [editEffectiveDaysWorked, workers]
  );

  const startEdit = (w: WageEntry) => {
      setEditingId(w.id);
      setEditPaid(Boolean(w.paid));
      setEditPaidDate((w as any).paidDate || "");
      setEditPaidMethod(((w as any).paidMethod as any) || "");
      setEditPaidNotes((w as any).paidNotes || "");

      // ✅ load day flags
      const f = flagsFromEntry(w);
      setEditMon(Number(f.mon) === 1);
      setEditTue(Number(f.tue) === 1);
      setEditWed(Number(f.wed) === 1);
      setEditThu(Number(f.thu) === 1);
      setEditFri(Number(f.fri) === 1);
      setEditSat(Number(f.sat) === 1);
      setEditSun(Number(f.sun) === 1);

      // ✅ infer manual override if stored daysWorked doesn't match checkbox count
      const flagCount =
        Number(f.mon) + Number(f.tue) + Number(f.wed) + Number(f.thu) + Number(f.fri) + Number(f.sat) + Number(f.sun);

      const storedDays = Number(w.daysWorked || 0);
      if (storedDays !== Number(flagCount || 0) && storedDays > 0) {
        setEditManualDaysWorked(String(storedDays));
      } else {
        setEditManualDaysWorked("");
      }
    };


    const cancelEdit = () => {
    setEditingId(null);
    setEditPaid(false);
    setEditPaidDate("");
    setEditPaidMethod("");
    setEditPaidNotes("");

    setEditMon(false);
    setEditTue(false);
    setEditWed(false);
    setEditThu(false);
    setEditFri(false);
    setEditSat(false);
    setEditSun(false);
    setEditManualDaysWorked("");
  };


  const saveEdit = (w: WageEntry) => {
    // ✅ require at least one day selected (keeps data meaningful)
    const newFlags = flagsFromForm(editMon, editTue, editWed, editThu, editFri, editSat, editSun);
    if (!hasAnyDayFlag(newFlags)) {
      t("wages.atLeastOneDayAlert");
      return;
    }

    const next: any = { ...w };

    // ✅ update day flags
    next.mon = newFlags.mon;
    next.tue = newFlags.tue;
    next.wed = newFlags.wed;
    next.thu = newFlags.thu;
    next.fri = newFlags.fri;
    next.sat = newFlags.sat;
    next.sun = newFlags.sun;

    // ✅ update daysWorked + totalWages (keep existing adjustments)
    next.daysWorked = Number(editEffectiveDaysWorked || 0);
    next.totalWages = Number(editComputedTotal(w).toFixed(2));

    // ✅ payment fields
    next.paid = !!editPaid;
    if (editPaid) {
      next.paidDate = editPaidDate || new Date().toISOString().slice(0, 10);
      next.paidMethod = editPaidMethod || (next.paidMethod ?? undefined);
      next.paidNotes = editPaidNotes.trim() ? editPaidNotes.trim() : undefined;
    } else {
      next.paidDate = undefined;
      next.paidMethod = undefined;
      next.paidNotes = undefined;
    }

    updateWageEntry(next);
    cancelEdit();
  };


  const doDelete = (w: WageEntry) => {
    const ok = window.confirm(t("wages.deleteConfirm"));
    if (!ok) return;
    deleteWageEntry(w.id);
    if (editingId === w.id) cancelEdit();
  };

  type DayField = readonly [label: string, checked: boolean, setter: React.Dispatch<React.SetStateAction<boolean>>];

  const dayFields: DayField[] = [
    [t("wages.dayMon"), mon, setMon],
    [t("wages.dayTue"), tue, setTue],
    [t("wages.dayWed"), wed, setWed],
    [t("wages.dayThu"), thu, setThu],
    [t("wages.dayFri"), fri, setFri],
    [t("wages.daySat"), sat, setSat],
    [t("wages.daySun"), sun, setSun],
  ];

  const manualDaysParsed = parseOptionalNumber(manualDaysWorked);

  return (
    <div className="container">
      <style>{`
        .wageStack { display: grid; gap: 14px; }

        @media (max-width: 980px) {
          .wageTopGrid { grid-template-columns: 1fr !important; }
          .wageHideMd { display: none !important; }
          .wageRow2 { grid-template-columns: 1fr !important; }
          .wageRow5 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 680px) {
          .wageHideSm { display: none !important; }
          .wageRow5 { grid-template-columns: 1fr !important; }
        }
        .wageField { min-width: 0; }
        textarea.input { box-sizing: border-box; width: 100%; max-width: 100%; }

        .wageFilters{
          display: grid;
          /* Week is column 1 (wider), then Project/Worker/Paid, then Clear */
          grid-template-columns: minmax(320px, 1.8fr) repeat(3, minmax(140px, 1fr)) auto;
          gap: 12px;
          align-items: end;
          width: min(1100px, 100%);
          justify-self: end;
        }

        .wageFilterItem{ min-width: 0; }
        @media (max-width: 980px){
          .wageFilters{
            grid-template-columns: 1fr 1fr;
            width: 100%;
            justify-self: stretch;
          }
          /* Week spans full width on tablet so it never truncates */
          .wageFilterWeek{ grid-column: 1 / -1; }
        }

        @media (max-width: 680px){
          .wageFilters{ grid-template-columns: 1fr; }
        }

        .wageLedger table { font-size: 13px; }
        .wageLedger thead th { padding: 10px 12px; }
        .wageLedger tbody td { padding: 10px 12px; }

        .tableIconBtn {
          height: 36px;
          width: 36px;
          display: grid;
          place-items: center;
          border-radius: 999px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          color: var(--text);
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
          transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
        }
        .tableIconBtn:hover {
          background: rgba(255,255,255,1);
          transform: translateY(-1px);
          border-color: rgba(15, 23, 42, 0.18);
        }
        .tableIconBtn:active { transform: translateY(0px); }
        .tableIconBtnDanger {
          border-color: rgba(229, 83, 61, 0.28);
          background: rgba(229, 83, 61, 0.08);
          color: var(--bad);
          box-shadow: none;
        }
        .tableIconBtnDanger:hover {
          background: rgba(229, 83, 61, 0.12);
          border-color: rgba(229, 83, 61, 0.40);
        }
        /* ---------- Ledger table sizing ---------- */
        .wageLedgerTable{
          width: 100%;
          table-layout: fixed; /* KEY: prevents columns blowing up */
        }
        .wageLedgerTable th, .wageLedgerTable td{
          vertical-align: top;
        }

        /* Column widths */
        .colWeek     { width: 165px; }  /* squeezed */
        .colProject  { width: 190px; }  /* still 2 lines ok */
        .colWorker   { width: 120px; }  /* slightly smaller */
        .colDays     { width: 52px;  }
        .colDow      { width: 150px; }  /* still supports 3+3 pills */
        .colTotal    { width: 100px; }
        .colPaid     { width: 82px;  }
        .colPaidDate { width: 98px;  }  /* squeezed */
        .colActions  { width: 96px;  }  /* icon buttons still fit */

        /* Align Days + Total right */
        .wageLedgerTable td:nth-child(4),
        .wageLedgerTable th:nth-child(4),
        .wageLedgerTable td:nth-child(6),
        .wageLedgerTable th:nth-child(6){
          text-align: right;
        }

        /* ---------- Days-of-week pills ---------- */
        .dowPills{
          display: grid;
          grid-template-columns: repeat(3, max-content); /* 3 per row */
          gap: 6px 8px;
          align-content: start;
        }
        .dowPill{
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 22px;
          padding: 0 10px;
          border-radius: 999px;
          border: 1px solid var(--border);
          background: rgba(80, 140, 255, 0.14);
          font-weight: 900;
          font-size: 12px;
          color: var(--text);
        }

        /* ---------- Add form day checkboxes: responsive grid ---------- */
        .dayCheckboxGrid{
          display: grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          gap: 8px;
        }
        @media (max-width: 980px){
          .dayCheckboxGrid{
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
        }
        @media (max-width: 680px){
          .dayCheckboxGrid{
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        /* ---------- Mobile: let the table scroll instead of crushing ---------- */
        .wageLedger{
          overflow-x: auto;
        }
        .wageLedger table{
          min-width: 980px; /* horizontal scroll on small screens */
        }
  
      `}</style>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>{t("wages.pageTitle")}</h1>
          <div className="pageNote">{t("wages.pageNote")}</div>
        </div>

        <div style={{ minWidth: 320, maxWidth: 520, flex: "1 1 360px" }}>
          <label className="label">{t("common.search")}</label>
          <input
            className="input"
            placeholder={t("wages.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <div className="wageTopGrid" style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 14, alignItems: "start" }}>
        <Card
          title={t("wages.addTitle")}
          subtitle={
            <>
              <span className="reqStar">*</span> {t("common.required")}
            </>
          }
        >
          <div style={{ display: "grid", gap: 14 }}>
            <div className="wageRow2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
              <div className="field wageField">
                <label className="label">
                  {t("wages.workerLabel")} <span className="reqStar">*</span>
                </label>
                <select
                  value={resourceId}
                  onChange={(e) => {
                    setResourceId(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="">{t("wages.selectWorker")}</option>
                  {workers.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} ({money(Number((r as any).dayRate || 0))}/day)
                    </option>
                  ))}
                </select>
              </div>

              <div className="field wageField">
                <label className="label">
                  {t("wages.projectLabel")} <span className="reqStar">*</span>
                </label>
                <select
                  value={projectId}
                  onChange={(e) => {
                    setProjectId(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="">{t("wages.selectProject")}</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {projectDisplayLabelPretty(p as any, clients as any)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field wageField">
                <label className="label">
                  {t("wages.yearLabel")} <span className="reqStar">*</span>
                </label>
                <input
                  className="input"
                  type="number"
                  value={year}
                  onChange={(e) => {
                    setYear(Number(e.target.value));
                    if (formError) setFormError(null);
                  }}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="field wageField">
                <label className="label">
                  {t("wages.weekLabel")} <span className="reqStar">*</span>
                </label>
                <select
                  value={weekNumber}
                  onChange={(e) => {
                    setWeekNumber(Number(e.target.value));
                    if (formError) setFormError(null);
                  }}
                  style={{ width: "100%" }}
                >
                  {weekOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field wageField">
              <label className="label">{t("wages.daysWorkedLabel")}</label>
              <div className="dayCheckboxGrid">
                {dayFields.map(([label, checked, setter]) => (
                  <label
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      border: "1px solid var(--border)",
                      borderRadius: 12,
                      padding: "10px 10px",
                      background: checked ? "rgba(80, 140, 255, 0.10)" : "rgba(255,255,255,0.65)",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <input type="checkbox" checked={checked} onChange={(e) => setter(e.target.checked)} />
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>
                  </label>
                ))}
              </div>

              <div className="noteOnCard" style={{ marginTop: 8 }}>
                {t("wages.checkboxCount")} <b>{checkboxDaysWorked}</b>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 12, alignItems: "end" }}>
              <div className="field wageField">
                <label className="label">{t("wages.manualDaysLabel")}</label>
                <input
                  className="input"
                  type="number"
                  value={manualDaysWorked}
                  onChange={(e) => {
                    setManualDaysWorked(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  placeholder={t("wages.manualDaysPlaceholder")}
                  min={0}
                  style={{ width: "100%" }}
                />
                <div className="noteOnCard" style={{ marginTop: 8 }}>{t("wages.manualDaysNote")}</div>
              </div>

              <div className="field wageField">
                <label className="label">{t("wages.daysUsedLabel")}</label>
                <div style={{ padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 12, background: "rgba(255,255,255,0.65)" }}>
                  <div style={{ fontWeight: 900, fontSize: 16 }}>{effectiveDaysWorked}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>{manualDaysParsed !== null ? t("wages.manualOverride") : t("wages.checkboxCount2")}</div>
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
              <div className="field wageField">
                <label className="label">{t("wages.adjustmentsLabel")}</label>
                <input
                  className="input"
                  type="number"
                  value={adjustments}
                  onChange={(e) => {
                    setAdjustments(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  placeholder={t("wages.adjustmentsPlaceholder")}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="field wageField">
                <label className="label">{t("wages.computedTotalLabel")}</label>
                <div style={{ padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 12, background: "rgba(255,255,255,0.65)" }}>
                  <div style={{ fontWeight: 900, fontSize: 16 }}>{money(computedTotal)}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>
                    {effectiveDaysWorked} {t("wages.dayTimesRate")}{money(dayRate)} + {money(adjustmentsNum)}
                  </div>
                </div>
              </div>
            </div>

            <div className="field wageField">
              <label className="label">{t("wages.notesLabel")}</label>
              <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} style={{ width: "100%" }} />
            </div>
          </div>

          {formError && (
            <div className="formError" role="alert">
              {formError}
            </div>
          )}

          <div className="formActions">
            <button className="btn" onClick={handleAdd} type="button">{t("wages.addBtn")}</button>
            <button className="btn btnSecondary" onClick={resetAddForm} type="button">{t("common.clear")}</button>
          </div>
        </Card>

        <Card title={t("wages.payThisWeek")} subtitle={t("wages.payThisWeekSub")}>
          <div style={{ display: "grid", gap: 12 }}>
              {payWeekOverlapWarnings.length > 0 && (
                <div
                  style={{
                    border: "1px solid rgba(255, 180, 90, 0.55)",
                    background: "rgba(255, 180, 90, 0.14)",
                    borderRadius: 14,
                    padding: 12,
                    color: "var(--text)",
                    fontSize: 13,
                    lineHeight: 1.35,
                  }}
                >
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>{t("wages.overlapWarningTitle")}</div>

                  <div style={{ color: "var(--muted)", marginBottom: 8 }}>
                    {t("wages.overlapWarning")}
                    If a worker split a day across projects (e.g., 0.5 day + 0.5 day), use{" "}
                    <b>Manual Days Worked Override</b> so totals don’t overpay.
                  </div>

                  <ul style={{ margin: 0, paddingLeft: 18, color: "var(--muted)" }}>
                    {payWeekOverlapWarnings.map((w) => (
                      <li key={w.workerName}>
                        <b style={{ color: "var(--bad)", fontWeight: 900 }}>
                          {w.workerName}
                        </b>
                        <span style={{ color: "var(--muted)" }}>
                          {" — overlapping days: "}
                        </span>
                        <span style={{ color: "var(--bad)", fontWeight: 700 }}>
                          {w.days.join(", ")}
                        </span>
                        <span style={{ opacity: 0.9 }}>
                          ({w.entryCount} entries across {w.projectCount} project(s))
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
              <div className="field wageField">
                <label className="label">{t("wages.weekLabel2")}</label>
                <select value={payWeekKey} onChange={(e) => setPayWeekKey(e.target.value)} style={{ width: "100%" }}>
                  {availableWeeks.map((w) => (
                    <option key={`${w.year}-${w.weekNumber}`} value={`${w.year}-${w.weekNumber}`}>
                      {weekLabelWithDates(w.year, w.weekNumber, t("wages.weekLabel"), i18n.language)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="actionsRow" style={{ justifyContent: "flex-end", gap: 10 }}>
                <button className="btn btnSecondary" disabled={payCardRows.length === 0} onClick={() => markEntriesPaid(unpaidForPayWeekIds, "Cash")} type="button">
                  {t("wages.markWeekPaidCash")}
                </button>
                <button className="btn" disabled={payCardRows.length === 0} onClick={() => markEntriesPaid(unpaidForPayWeekIds, "Bank")} type="button">
                  {t("wages.markWeekPaidBank")}
                </button>
              </div>
            </div>

            {payCardRows.length === 0 ? (
              <div style={{ color: "var(--muted)", fontStyle: "italic" }}>{t("wages.noUnpaid")}</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {payCardRows.map((row) => (
                  <div
                    key={row.resourceId}
                    style={{
                      border: "1px solid var(--border)",
                      borderRadius: 14,
                      padding: 12,
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      alignItems: "center",
                      background: "rgba(255,255,255,0.55)",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 900 }}>{row.workerName}</div>
                      <div style={{ fontSize: 12, color: "var(--muted)" }}>
                        {row.projects.size} {t("common.project")}(s) • {row.entryIds.length} {row.entryIds.length === 1 ? t("wages.entryWord") : t("wages.entriesWord")}
                      </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontWeight: 900, fontSize: 16 }}>{money(row.total)}</div>
                      <div className="actionsRow" style={{ justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                        <button className="btn btnSecondary btnSm" onClick={() => markEntriesPaid(row.entryIds, "Cash")} type="button">
                          {t("wages.markPaidCash")}
                        </button>
                        <button className="btn btnSm" onClick={() => markEntriesPaid(row.entryIds, "Bank")} type="button">
                          {t("wages.markPaidBank")}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Totals (Filtered) */}
      <div className="wageStack" style={{ marginTop: 14 }}>
        <Card title={t("wages.totalsFiltered")} subtitle={t("wages.totalsSub")}>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div className="cardSub">{t("wages.rowsLabel")}</div>
              <div style={{ fontWeight: 900 }}>{totalsFiltered.rows}</div>
            </div>
            <div>
              <div className="cardSub">{t("wages.daysLabel")}</div>
              <div style={{ fontWeight: 900 }}>{totalsFiltered.days}</div>
            </div>
            <div>
              <div className="cardSub">{t("wages.paidLabel")}</div>
              <div className="numGood">{money(totalsFiltered.paid)}</div>
            </div>
            <div>
              <div className="cardSub">{t("wages.unpaidLabel")}</div>
              <div className="numWarn">{money(totalsFiltered.unpaid)}</div>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <div className="cardSub">{t("wages.totalLabel")}</div>
              <div className="numBad">{money(totalsFiltered.total)}</div>
            </div>
          </div>

          <div className="noteOnCard" style={{ marginTop: 8 }}>
            {t("wages.payTip")}
          </div>
        </Card>

        {/* LEDGER */}
        <div className="wageLedger">
          <TableShell title={t("wages.ledgerTitle")} subtitle={t("wages.ledgerSubtitle2", { count: visibleEntries.length })} right={ledgerHeaderRight}>
            <table className="wageLedgerTable">
            <colgroup>
              <col className="colWeek" />
              <col className="colProject" />
              <col className="colWorker" />
              <col className="colDays" />
              <col className="colDow" />
              <col className="colTotal" />
              <col className="colPaid" />
              <col className="colPaidDate" />
              <col className="colActions" />
            </colgroup>
              <thead>
                <tr>
                  <th style={{ width: 210 }}>{t("wages.colWeek")}</th>
                  <th>{t("wages.colProject")}</th>
                  <th style={{ width: 180 }}>{t("wages.colWorker")}</th>
                  <th style={{ width: 70, textAlign: "right" }}>{t("wages.colDays")}</th>
                  <th style={{ width: 260 }}>{t("wages.colDaysOfWeek")}</th>
                  <th style={{ width: 130, textAlign: "right" }}>{t("wages.colTotal")}</th>
                  <th style={{ width: 90 }}>{t("wages.colPaid")}</th>
                  <th className="wageHideMd" style={{ width: 120 }}>
                    {t("wages.colPaidDate")}
                  </th>
                  <th style={{ width: 150 }}>{t("common.actions")}</th>
                </tr>
              </thead>

              <tbody>
                {visibleEntries.map((w) => {
                  const isEditing = editingId === w.id;

                  return (
                    <React.Fragment key={w.id}>
                      <tr>
                        <td>
                          <CellClamp lines={2} title={weekLabelWithDates(w.year, w.weekNumber, t("wages.weekLabel"), i18n.language)}>
                            {weekLabelWithDates(w.year, w.weekNumber, t("wages.weekLabel"), i18n.language)}
                          </CellClamp>
                        </td>

                        <td>
                          <CellClamp lines={2} title={getProjectPretty(w.projectId)}>
                            {getProjectPretty(w.projectId)}
                          </CellClamp>
                        </td>

                        <td>
                          <CellClamp lines={2} title={getWorkerName(w.resourceId)}>
                            <span style={{ fontWeight: 900 }}>{getWorkerName(w.resourceId)}</span>
                          </CellClamp>
                        </td>

                        <td style={{ textAlign: "right" }}>{w.daysWorked}</td>

                        <td>{renderDaysOfWeek(w)}</td>

                        <td style={{ textAlign: "right" }}>
                          <span className="numBad">{money(w.totalWages)}</span>
                        </td>

                        <td>
                          {isEditing ? (
                            <select
                              value={editPaid ? "Yes" : "No"}
                              onChange={(e) => {
                                const nextPaid = e.target.value === "Yes";
                                setEditPaid(nextPaid);

                                // ✅ If user switches Paid → No, clear dependent fields
                                if (!nextPaid) {
                                  setEditPaidMethod("");
                                  setEditPaidNotes("");
                                  setEditPaidDate("");
                                }
                              }}
                              style={{ width: "100%" }}
                            >
                              <option value="No">{t("wages.noOption")}</option>
                              <option value="Yes">{t("wages.yesOption")}</option>
                            </select>
                          ) : w.paid ? (
                            <span className="badge badgeGood">{t("wages.paidBadge")}</span>
                          ) : (
                            <span className="badge badgeWarn">{t("wages.unpaidBadge")}</span>
                          )}

                        </td>

                        <td className="wageHideMd">
                          {isEditing ? (
                            <span style={{ color: "var(--muted)" }}>—</span>
                          ) : (
                            ((w as any).paidDate
                              ? new Date((w as any).paidDate + "T00:00:00").toLocaleDateString(undefined, {
                                  year: "2-digit",
                                  month: "2-digit",
                                  day: "2-digit",
                                })
                              : "—")
                          )}
                        </td>

                        <td>
                          {isEditing ? (
                            <span style={{ color: "var(--muted)", fontSize: 12 }}>{t("wages.editingLabel")}</span>
                          ) : (
                            <div className="actionsRow" style={{ gap: 10 }}>
                              <button
                                type="button"
                                className="tableIconBtn"
                                onClick={() => startEdit(w)}
                                title={t("wages.editEntry")}
                                aria-label={t("wages.editEntry")}
                              >
                                <IconPencil />
                              </button>
                              <button
                                type="button"
                                className="tableIconBtn tableIconBtnDanger"
                                onClick={() => doDelete(w)}
                                title={t("wages.deleteEntry")}
                                aria-label={t("wages.deleteEntry")}
                              >
                                <IconTrash />
                              </button>
                            </div>
                          )}
                        </td>


                      </tr>

                      {isEditing && (
                        <tr>
                          <td colSpan={9} style={{ padding: 14 }}>
                            <div
                              style={{
                                border: "1px solid var(--border)",
                                borderRadius: 14,
                                padding: 12,
                                background: "rgba(255,255,255,0.65)",
                                display: "grid",
                                gap: 12,
                                maxWidth: 820,
                              }}
                            >
                              <div style={{ fontWeight: 900 }}>{t("wages.editDaysLabel")}</div>

                              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, maxWidth: 560 }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <input type="checkbox" checked={editMon} onChange={(e) => setEditMon(e.target.checked)} /> {t("wages.dayMon")}
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <input type="checkbox" checked={editTue} onChange={(e) => setEditTue(e.target.checked)} /> {t("wages.dayTue")}
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <input type="checkbox" checked={editWed} onChange={(e) => setEditWed(e.target.checked)} /> {t("wages.dayWed")}
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <input type="checkbox" checked={editThu} onChange={(e) => setEditThu(e.target.checked)} /> {t("wages.dayThu")}
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <input type="checkbox" checked={editFri} onChange={(e) => setEditFri(e.target.checked)} /> {t("wages.dayFri")}
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <input type="checkbox" checked={editSat} onChange={(e) => setEditSat(e.target.checked)} /> {t("wages.daySat")}
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <input type="checkbox" checked={editSun} onChange={(e) => setEditSun(e.target.checked)} /> {t("wages.daySun")}
                                </label>
                              </div>

                              <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 1fr) minmax(240px, 1fr)", gap: 12 }}>
                                <div>
                                  <label className="label" style={{ marginBottom: 6 }}>
                                    {t("wages.manualDaysOverrideLabel")}
                                  </label>
                                  <input
                                    className="input"
                                    type="number"
                                    min={0}
                                    placeholder={t("wages.manualDaysEditPlaceholder")}
                                    value={editManualDaysWorked}
                                    onChange={(e) => setEditManualDaysWorked(e.target.value)}
                                    style={{ width: "100%" }}
                                  />
                                  <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>
                                    {t("wages.daysUsedPreview", { days: editEffectiveDaysWorked })}{" "}
                                    <b style={{ color: "var(--text)" }}>{money(editComputedTotal(w))}</b>
                                  </div>
                                </div>

                                <div style={{ display: "grid", gap: 10 }}>
                                  <div>
                                    <label className="label" style={{ marginBottom: 6 }}>
                                      {t("wages.methodLabel3")}
                                    </label>
                                    <select value={editPaidMethod} 
                                      onChange={(e) => setEditPaidMethod(e.target.value as any)} 
                                      style={{ width: "100%" }}
                                      disabled={!editPaid}
                                      >
                                      <option value="">—</option>
                                      <option value="Cash">{t("common.cash")}</option>
                                      <option value="Bank">{t("common.bank")}</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="label" style={{ marginBottom: 6 }}>
                                      {t("wages.paidNotesLabel2")}
                                    </label>
                                    <input className="input" 
                                      value={editPaidNotes} 
                                      onChange={(e) => setEditPaidNotes(e.target.value)} 
                                      style={{ width: "100%" }} 
                                      disabled={!editPaid}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="actionsRow" style={{ justifyContent: "flex-end", gap: 10 }}>
                                <button className="btn btnSm" onClick={() => saveEdit(w)} type="button">{t("wages.saveChanges")}</button>
                                <button className="btn btnSecondary btnSm" onClick={cancelEdit} type="button">{t("common.cancel")}</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {visibleEntries.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: 14, color: "var(--muted)", fontStyle: "italic" }}>
                      {t("wages.noWages")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableShell>
        </div>
      </div>
    </div>
  );
}


